import { HttpClient } from "@angular/common/http";
import { Injectable, computed, inject, signal } from "@angular/core";
import { Observable, catchError, map, of, tap } from "rxjs";

import { i18nAssetUrl } from "./i18n-asset-url";

/** One language's translated-key counts, as `tools/build-i18n-coverage.js` emits them. */
export interface LanguageCoverage {
  /** Chrome: buttons, surface names, the plan ceiling — the strings every screen shows. */
  readonly root: number;
  readonly rootTotal: number;
  /** Navigation: the `shell` scope alone. What a reader judges the language by. */
  readonly nav: number;
  readonly navTotal: number;
  /** Screens: every lazy scope, navigation included. */
  readonly scopes: number;
  readonly scopesTotal: number;
}

export interface CoverageManifest {
  readonly rootTotal: number;
  readonly navTotal: number;
  readonly scopesTotal: number;
  readonly languages: Readonly<Record<string, LanguageCoverage>>;
}

/** What a screen needs to describe one language in a sentence. */
export interface CoverageReading {
  readonly known: boolean;
  /** 0–100, rounded. */
  readonly chromePercent: number;
  readonly navPercent: number;
  readonly screensPercent: number;
  /** Nothing translated at all — the portal will read entirely in English. */
  readonly untranslated: boolean;
  /** Chrome done, screens largely not: a translated shell around English pages. */
  readonly chromeOnly: boolean;
  /**
   * The menus are still English.
   *
   * Separated from `untranslated` because it is the state that gets reported as a bug: the
   * chrome translates, nothing visible changes, and the language looks broken. Naming it lets
   * a picker say so at the moment of choosing.
   */
  readonly navMissing: boolean;
}

const EMPTY: CoverageReading = {
  known: false,
  chromePercent: 0,
  navPercent: 0,
  screensPercent: 0,
  untranslated: true,
  chromeOnly: false,
  navMissing: false
};

/**
 * How much of the portal each language actually carries.
 *
 * Read from a generated manifest rather than computed at runtime, because the only honest
 * source is the translation files themselves and the app does not have them — it fetches one
 * scope at a time, on demand. `tools/build-i18n-coverage.js` walks all of them at build time
 * and writes `assets/i18n/coverage.json`.
 *
 * The Languages screen used to state that this figure "would need a manifest the build does
 * not emit, so every figure would be a guess". It emits one now, so the screen can stop
 * apologising and start answering the question a tenant actually has: if I offer Assamese,
 * what will my people see?
 *
 * A failed or missing fetch reports `known: false` rather than zero. "We could not tell" and
 * "nothing is translated" are different answers and only one of them should stop a tenant
 * enabling a language.
 */
@Injectable({ providedIn: "root" })
export class LanguageCoverageService {
  private readonly http = inject(HttpClient);

  private readonly manifest = signal<CoverageManifest | null>(null);
  private inFlight = false;

  readonly loaded = computed(() => this.manifest() !== null);

  load(): Observable<CoverageManifest | null> {
    const cached = this.manifest();
    if (cached !== null || this.inFlight) {
      return of(cached);
    }
    this.inFlight = true;
    return this.http.get<CoverageManifest>(i18nAssetUrl("assets/i18n/coverage.json")).pipe(
      tap((value) => {
        this.manifest.set(value);
        this.inFlight = false;
      }),
      map((value): CoverageManifest | null => value),
      catchError(() => {
        this.inFlight = false;
        return of(null);
      })
    );
  }

  /**
   * `chromeOnly` is the state most languages are in and the one worth naming: the shell,
   * the navigation and every button read in the reader's language while the screens
   * themselves are still English. It is a usable state, not a broken one, and a tenant
   * deciding whether to offer a language should be told which of the two they are getting.
   */
  reading(code: string): CoverageReading {
    const manifest = this.manifest();
    if (manifest === null) {
      return EMPTY;
    }
    const row = manifest.languages[code];
    if (row === undefined) {
      return EMPTY;
    }
    const chromePercent = percent(row.root, row.rootTotal);
    const navPercent = percent(row.nav, row.navTotal);
    const screensPercent = percent(row.scopes, row.scopesTotal);
    return {
      known: true,
      chromePercent,
      navPercent,
      screensPercent,
      untranslated: chromePercent === 0 && screensPercent === 0,
      chromeOnly: chromePercent >= 90 && screensPercent < 50,
      navMissing: navPercent < 50
    };
  }
}

function percent(done: number, total: number): number {
  return total === 0 ? 0 : Math.round((done / total) * 100);
}
